import axios from "axios";
import { Auth } from "aws-amplify";
import { BACKEND_ENDPOINT, CREATE_REQUEST , UPDATE_REQUEST , DELETE_REQUEST , GET_REQUESTS , GET_REQUEST_DETAIL } from "./urls_dev";

const getToken = () => {
  const name = "VCS_TOKEN=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookies = decodedCookie.split(";");

  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.startsWith(name)) {
      return cookie.substring(name.length);
    }
  }

  return null;
};

/**
 * Backend timestamps are unix **seconds** (see `Timestamp`/`createdOn` below),
 * but `new Date()` expects milliseconds — passing seconds straight in dated
 * every row to Jan 1970. Scale seconds up, pass millisecond values through.
 */
const toIsoTimestamp = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  const ms = n < 1e11 ? n * 1000 : n; // < ~year 5138 in seconds → it's seconds
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

export const requestDefinition = {
  id: "",
  requestedFor: "",
  description: "",
  status: "pending",
  motor: null,
  propeller: null,
  esc: null,
  battery: null,
  flightController: null,
  createdBy: JSON.parse(localStorage.getItem("usda"))?.email,
  updatedBy: "",
  createdOn: Math.floor(Date.now() / 1000),
  updatedOn: Math.floor(Date.now() / 1000),
  isDeleted: false,
  EntityType: "REQUEST",
  Timestamp: Math.floor(Date.now() / 1000),
  ChangeLog: "created by the user",
};

export const getRequests = async (params = {}) => {
  const token = getToken();
  const backendUrl = `${BACKEND_ENDPOINT}${GET_REQUESTS}`;

  try {
    const response = await axios.post(backendUrl, 
        {
        operation: "LIST",
        entityType: "REQUEST",
        data: {
          parentId: JSON.parse(localStorage.getItem("usda"))?.email,
          parentType: "REQUEST",
          limit: params.limit || 100,
          page: params.page || 1,
          lastEvaluatedKey: params.lastEvaluatedKey || null,
          
        },
      },
        {
      headers: {
        Authorization: token,
        "Content-Type": "application/json"
      },
    });
        
    // Gateway may return `data` as a JSON string (same as getDevices).
    const rawData = typeof response.data.data === "string"
      ? JSON.parse(response.data.data)
      : response.data;
    const items = Array.isArray(rawData) ? rawData : rawData.data || [];

    return items.map((item) => ({
      ...item,
      timestamp: toIsoTimestamp(item.Timestamp ?? item.createdOn),
    }));
  } catch (error) {
    console.error("Error fetching requests:", error);
    throw error;
  }
};

export const getRequestDetails = async (requestId) => {
  if (!requestId) {
    throw new Error("A request ID is required to fetch request details");
  }

  const token = getToken();
  const backendUrl = `${BACKEND_ENDPOINT}${GET_REQUEST_DETAIL}`;

  try {
    const response = await axios.post(
      backendUrl,
      {
        operation: "READ",
        entityType: "REQUEST",
        data: { requestId, serviceName: "thruststand" },
      },
      {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      }
    );

    // The gateway sometimes returns the payload as a JSON string (see
    // getDeviceDetails) — unwrap both shapes before reading fields.
    const responseData = typeof response.data.body === "string"
      ? JSON.parse(response.data.body)
      : response.data;
    const requestData = typeof responseData.data === "string"
      ? JSON.parse(responseData.data)
      : responseData.data || responseData;

    return {
      ...requestData,
      timestamp: toIsoTimestamp(requestData.createdOn ?? requestData.Timestamp),
    };
  } catch (error) {
    console.error("Error fetching request details:", error);
    throw error;
  }
};

export const createRequest = async (requestData) => {
  const token = getToken();
  const currentUser = await Auth.currentAuthenticatedUser();
  const backendUrl = `${BACKEND_ENDPOINT}${CREATE_REQUEST}`;
  
  const newRequest = {
    ...requestDefinition,
    ...requestData,
    "association:USER": [
      {
        id: "cognitoId",
        value: currentUser.attributes.sub,
        entityType: "USER",
      },
    ],
    createdBy: currentUser.attributes.email,
    updatedBy: currentUser.attributes.email
  };

  const body = {
    operation: "CREATE",
    entityType: "REQUEST",
    data: newRequest,
  };

  try {
    const response = await axios.post(
        backendUrl,
        body,
      {
        headers: {
          Authorization: token,
          "Content-Type": "application/json"
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error creating request:", error);
    throw error;
  }
};

export const updateRequest = async (requestData) => {
  const token = getToken();
  const currentUser = await Auth.currentAuthenticatedUser();
    const backendUrl = `${BACKEND_ENDPOINT}${UPDATE_REQUEST}`;
  
  const updatedRequest = {
    ...requestData,
    updatedBy: currentUser.attributes.email,
    serviceName: "thruststand",
    updatedOn: Math.floor(Date.now() / 1000),
    ChangeLog: "updated by user"
  };

  const body = {
    operation: "UPDATE",
    entityType: "REQUEST",
    data: updatedRequest,
  };

  try {
    const response = await axios.post(
        backendUrl,
        body,
      {
        headers: {
          Authorization: token,
          "Content-Type": "application/json"
        }
      }
    );

    return response.data;
  } catch (error) {
    console.error("Error updating request:", error);
    throw error;
  }
};


export const deleteRequest = async (requestId) => {
  const token = getToken();
  const backendUrl = `${BACKEND_ENDPOINT}${DELETE_REQUEST}`;
  const body = JSON.stringify({
    operation: "DELETE",
    entityType: "REQUEST",
    data: {requestId},
  });
  try {
    const response = await axios.post(
        backendUrl,
        body,
        {
        headers: {
          Authorization: token,
          "Content-Type": "application/json"
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting request:", error);
    throw error;
  }
};

export const getRequestStatusOptions = async () => {
  return [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'delivered', label: 'Delivered' }
  ];
};

export const getEquipmentOptions = async () => {
  return {
    motor: {
      kv: [
        { value: "1700kv", label: "1700KV" },
        { value: "2200kv", label: "2200KV" }
      ],
      size: [
        { value: "2207", label: "2207" },
        { value: "2306", label: "2306" }
      ],
      brand: [
        { value: "t-motor", label: "T-Motor" },
        { value: "brotherhobby", label: "BrotherHobby" }
      ]
    },
    propeller: {
      size: [
        { value: "5x4.3", label: "5x4.3" },
        { value: "7x4x3", label: "7x4x3" }
      ],
      material: [
        { value: "carbon-fiber", label: "Carbon Fiber" },
        { value: "polycarbonate", label: "Polycarbonate" }
      ]
    },
    esc: {
      amperage: [
        { value: "30a", label: "30A" },
        { value: "45a", label: "45A" }
      ],
      cells: [
        { value: "4s", label: "4S" },
        { value: "6s", label: "6S" }
      ],
      protocol: [
        { value: "dshot600", label: "DShot600" },
        { value: "pwm", label: "PWM" }
      ]
    },
    battery: {
      capacity: [
        { value: "1300mah", label: "1300mAh" },
        { value: "1500mah", label: "1500mAh" }
      ],
      cells: [
        { value: "4s", label: "4S" },
        { value: "6s", label: "6S" }
      ],
      cRating: [
        { value: "75c", label: "75C" },
        { value: "95c", label: "95C" }
      ]
    },
    flightController: {
      mcu: [
        { value: "f4", label: "F4" },
        { value: "f7", label: "F7" }
      ],
      gyro: [
        { value: "mpu6000", label: "MPU6000" },
        { value: "icm20602", label: "ICM20602" }
      ]
    }
  };
};